import React, { useEffect, useRef, useState } from 'react';
import { Send, MessageSquare, Trash2, Users } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  listPortalMessages, sendPortalMessage, markPortalMessagesRead,
  listThreadParticipants, uploadMessageAttachment, softDeletePortalMessage,
} from '../../lib/db';
import { supabase } from '../../services/supabaseClient';
import toast from '../../lib/toast';
import MessageAttachments, { PendingAttachments, AttachButton } from '../../components/messages/MessageAttachments';
import MessageReactions, { useMessageReactions } from '../../components/messages/MessageReactions';

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' +
    d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function fullName(u) {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ').trim() || u?.email || 'Team member';
}

export default function PortalMessagesPage() {
  const { contact, firm } = usePortal();
  const { user }          = useAuth();
  const [messages, setMessages] = useState([]);
  const [body,     setBody]     = useState('');
  const [loading,  setLoading]  = useState(true);
  const [sending,  setSending]  = useState(false);
  const [participants,  setParticipants]  = useState([]);
  const [pendingFiles,  setPendingFiles]  = useState([]);
  const [deletingId,    setDeletingId]    = useState(null);
  const bottomRef = useRef(null);

  const portalMsgIds = messages.map((m) => m.id).filter((id) => !String(id).startsWith('temp-'));
  const { byMsg: reactionsByMsg, refresh: refreshReactions } = useMessageReactions('portal', portalMsgIds);

  useEffect(() => {
    if (!contact) return undefined;
    let isMounted = true;

    listPortalMessages(contact.id).then(({ data }) => {
      if (!isMounted) return;
      setMessages(data || []);
      setLoading(false);
      markPortalMessagesRead(contact.id);
    });

    // Surface which firm staff are assigned to this thread so the client knows
    // who's listening (closes the "show assigned staff" UX gap).
    listThreadParticipants(contact.id).then(({ data }) => {
      if (!isMounted) return;
      setParticipants(data || []);
    });

    // Realtime — keep the thread in sync without a refresh, AND mark
    // newly-arrived firm messages as read so the firm-side badge clears too.
    const sub = supabase
      .channel(`portal_client_msgs_${contact.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'portal_messages',
        filter: `contact_id=eq.${contact.id}`,
      }, (payload) => {
        const msg = payload.new;
        if (!isMounted) return;
        // Skip own client messages (added optimistically already).
        if (msg.sender_type === 'client' && msg.sender_id === user?.id) return;
        setMessages((prev) => prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]);
        // Mark firm messages we just received as read.
        if (msg.sender_type === 'firm') markPortalMessagesRead(contact.id);
      })
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'portal_messages',
        filter: `contact_id=eq.${contact.id}`,
      }, (payload) => {
        const msg = payload.new;
        if (!isMounted) return;
        setMessages((prev) => prev.map((m) => m.id === msg.id
          ? { ...m, body: msg.body, attachments: msg.attachments, deleted_at: msg.deleted_at, read_at: msg.read_at }
          : m
        ));
      })
      .subscribe();

    return () => {
      isMounted = false;
      supabase.removeChannel(sub);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    const text = body.trim();
    const files = pendingFiles;
    if ((!text && files.length === 0) || !contact) return;
    setSending(true);

    const msgId = (window.crypto?.randomUUID && window.crypto.randomUUID())
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const temp = {
      id:          msgId,
      body:        text || null,
      sender_type: 'client',
      sender_id:   user?.id,
      created_at:  new Date().toISOString(),
      attachments: [],
      _optimistic: true,
    };
    setMessages((prev) => [...prev, temp]);
    setBody('');
    setPendingFiles([]);

    const attachments = [];
    for (const file of files) {
      const { data: a, error: upErr } = await uploadMessageAttachment(
        'portal',
        { firmId: contact.firm_id, contactId: contact.id, messageId: msgId },
        file,
      );
      if (upErr) {
        toast.error(`Upload failed (${file.name}): ${upErr.message}`);
        setSending(false);
        setMessages((prev) => prev.filter((m) => m.id !== msgId));
        setBody(text);
        setPendingFiles(files);
        return;
      }
      attachments.push(a);
    }

    const { data, error } = await sendPortalMessage(
      contact.firm_id, contact.id, user?.id, 'client',
      { id: msgId, body: text || null, attachments },
    );
    setSending(false);
    if (error) {
      toast.error('Failed to send message: ' + error.message);
      setMessages((prev) => prev.filter((m) => m.id !== msgId));
      setBody(text);
      setPendingFiles(files);
    } else {
      setMessages((prev) => prev.map((m) => m.id === msgId ? data : m));
    }
  };

  const handleDelete = async (msg) => {
    if (!window.confirm('Delete this message?')) return;
    setDeletingId(msg.id);
    const { error } = await softDeletePortalMessage(msg.id);
    setDeletingId(null);
    if (error) { toast.error('Could not delete: ' + error.message); return; }
    setMessages((prev) => prev.map((m) =>
      m.id === msg.id ? { ...m, deleted_at: new Date().toISOString(), body: null, attachments: [] } : m
    ));
  };

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div>
          <div className="h-8 w-32 bg-slate-200 dark:bg-slate-700 rounded mb-2" />
          <div className="h-4 w-48 bg-slate-200 dark:bg-slate-700 rounded" />
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={i % 2 === 0 ? 'flex justify-start' : 'flex justify-end'}>
              <div className={`h-12 ${i % 2 === 0 ? 'w-2/3' : 'w-1/2'} bg-slate-200 dark:bg-slate-700 rounded-2xl`} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const participantUsers = participants.map((p) => p.user).filter(Boolean);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Messages</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Direct thread with {firm?.name || 'your firm'}
        </p>
      </div>

      {/* Assigned staff strip — only renders when at least one firm member is
          assigned to this thread. Gives the client a clear sense of who's
          on the case. */}
      {participantUsers.length > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
          <Users size={13} className="text-slate-400" />
          <span className="text-xs text-slate-500 dark:text-slate-400">Assigned to you:</span>
          <div className="flex items-center -space-x-1.5">
            {participantUsers.slice(0, 5).map((u) => {
              const n   = fullName(u);
              const ini = n.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
              return (
                <div key={u.id} title={n}
                  className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold">
                  {ini}
                </div>
              );
            })}
            {participantUsers.length > 5 && (
              <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 border-2 border-white dark:border-slate-900 flex items-center justify-center text-[9px] font-semibold text-slate-500">
                +{participantUsers.length - 5}
              </div>
            )}
          </div>
          <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
            {participantUsers.map(fullName).join(', ')}
          </span>
        </div>
      )}

      {/* Message thread */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl flex flex-col overflow-hidden" style={{ minHeight: '460px', maxHeight: '60vh' }}>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-12 text-slate-400">
              <MessageSquare size={36} className="mb-3 opacity-30" />
              <p className="text-sm">No messages yet. Say hello!</p>
            </div>
          ) : (
            messages.map((msg) => {
              const isClient = msg.sender_type === 'client';
              const isOwn    = isClient && msg.sender_id === user?.id;
              const canDelete = !msg.deleted_at && !msg._optimistic && isOwn;
              return (
                <div key={msg.id} className={`flex ${isClient ? 'justify-end' : 'justify-start'} group`}>
                  <div className={`max-w-[75%] ${isClient ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                    {!isClient && (
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 px-1">
                        {firm?.name || 'Your attorney'}
                      </span>
                    )}
                    {msg.deleted_at ? (
                      <div className="px-4 py-2 rounded-2xl text-xs italic bg-slate-100 dark:bg-slate-800 text-slate-400">
                        Message deleted
                      </div>
                    ) : (
                      <div className="flex items-end gap-1.5">
                        {canDelete && (
                          <button
                            onClick={() => handleDelete(msg)}
                            disabled={deletingId === msg.id}
                            title="Delete message"
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50"
                          >
                            <Trash2 size={12} />
                          </button>
                        )}
                        <div className={`flex flex-col ${isClient ? 'items-end' : 'items-start'}`}>
                          {msg.body && (
                            <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap
                              ${isClient
                                ? 'bg-indigo-600 text-white rounded-br-sm'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-bl-sm'
                              }`}>
                              {msg.body}
                            </div>
                          )}
                          <MessageAttachments attachments={msg.attachments} />
                        </div>
                      </div>
                    )}
                    {!msg.deleted_at && !msg._optimistic && (
                      <MessageReactions
                        scope="portal"
                        messageId={msg.id}
                        firmId={contact.firm_id}
                        userId={user?.id}
                        reactions={reactionsByMsg.get(msg.id) || []}
                        onChange={refreshReactions}
                      />
                    )}
                    <span className="text-[11px] text-slate-400 px-1">{formatTime(msg.created_at)}</span>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Compose */}
        <form onSubmit={send} className="border-t border-slate-100 dark:border-slate-800 p-3">
          <PendingAttachments
            pending={pendingFiles.map((f) => ({ file: f, mime: f.type }))}
            onRemove={(idx) => setPendingFiles((cur) => cur.filter((_, i) => i !== idx))}
          />
          <div className="flex gap-2 items-center">
            <AttachButton
              disabled={sending}
              onSelect={(files) => setPendingFiles((cur) => [...cur, ...files])}
            />
            <input
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Type a message…"
              className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3.5 py-2.5 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(e); } }}
            />
            <button
              type="submit"
              disabled={(!body.trim() && pendingFiles.length === 0) || sending}
              className="flex items-center gap-1.5 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition"
            >
              <Send size={14} />
              <span className="hidden sm:inline">Send</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
